import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import { AnfitriaoDosBloqueios } from '../bloqueios/AnfitriaoDosBloqueios';
import { AvisoDeAnalise } from '../analise/AvisoDeAnalise';
import { iniciarAnalise } from '../analise/contentsquare';
import { iniciarUso } from '../analise/uso';
import { BrandProvider, configureDomain, SessionProvider, useBrand } from '@eva/domain';
import { marcaDetectada as detection } from '../marca-detectada';
import { applyBrandTokens, CascaEsqueleto, createEvaTheme, tokensFromBrand } from '@eva/ui';
import { GuardaDePorta } from './GuardaDePorta';
import { aplicarMarcaNaAba } from './marcaNaAba';
import { avisarRotaACasca, EM_MOLDURA } from './moldura';
import { MolduraDoCelular } from './MolduraDoCelular';
import { preloadPortal, router } from './router';

configureDomain({
  apiUrl: import.meta.env.VITE_API_URL || '',
  storagePrefix: 'eva_app',
  legacy: {
    patientUrl: import.meta.env.VITE_LEGACY_PATIENT_URL || null,
    adminUrl: import.meta.env.VITE_LEGACY_ADMIN_URL || null,
  },
});


// No computador, `/eu` é a casca com a moldura de celular (15/10/2026): o app
// de verdade roda no quadro, e é ele quem conta o uso — a casca não conta.
if (!EM_MOLDURA) {
  // Análise de uso (Contentsquare): desligada sem `VITE_CONTENTSQUARE_TAG_ID` e sem consentimento.
  iniciarAnalise(router);
  // Análise de produto (A174): telas, sessões e o envio dos eventos de uso.
  iniciarUso(router);
  // Dentro da moldura: a casca acompanha o endereço.
  avisarRotaACasca(router);
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000, retry: 1, refetchOnWindowFocus: false } },
});

/** O tema segue a marca: tokens no :root e o createTheme do Mantine. */
function ThemedApp() {
  const { brand, resolvida } = useBrand();
  const tokens = useMemo(() => tokensFromBrand(brand.branding?.colors ?? null), [brand]);
  // Layout effect, não effect: os tokens vão para o :root ANTES do primeiro
  // frame. Com `useEffect` havia um frame pintado com os valores do
  // tokens.css — a Eva — mesmo quando a marca já era conhecida.
  useLayoutEffect(() => applyBrandTokens(tokens), [tokens]);
  // A marca resolvida — inclusive a da porta por caminho — na aba (A079).
  useEffect(() => {
    if (resolvida) aplicarMarcaNaAba(brand);
  }, [brand, resolvida]);
  const theme = useMemo(() => createEvaTheme(tokens), [tokens]);

  // Primeira visita a esta porta: a cor ainda é desconhecida. Esqueleto
  // neutro em vez de chutar a Eva e trocar depois.
  if (!resolvida) return <CascaEsqueleto />;
  if (EM_MOLDURA) return <MolduraDoCelular />;

  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <Notifications position="top-center" />
      <ModalsProvider>
        <SessionProvider prepararPortal={preloadPortal}>
          <GuardaDePorta />
          <RouterProvider router={router} />
          {/* O aviso dos bloqueios da marca, para qualquer tela (14/10/2026). */}
          <AnfitriaoDosBloqueios />
          <AvisoDeAnalise />
        </SessionProvider>
      </ModalsProvider>
    </MantineProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrandProvider detection={detection}>
        <ThemedApp />
      </BrandProvider>
    </QueryClientProvider>
  );
}
